import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

interface IEntityPaneHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailingContent?: React.ReactNode;
  avatar?: React.ReactNode;
  /**
   * Optional row rendered inside the header, below the title row (e.g. a tab
   * bar). Because it lives inside the header, it stays pinned with the title
   * and the scroll-driven drop shadow falls below it instead of between it and
   * the title.
   */
  belowContent?: React.ReactNode;
}

/**
 * Title row that sits above EntityPaneBody. Mirrors the Figma "Section Header"
 * component: title in `text-xl` semibold on mobile and `text-display-sm`
 * semibold at md+, mobile container `py-2` (8px each), md+ container
 * `pt-8 pb-3` (32px / 12px).
 *
 * Once the page scroller passes the shrink threshold (16px), a drop shadow
 * appears below the header at every viewport — darker below lg, much lighter
 * at lg+ (per Figma) — and at lg+ the top padding snaps from 48px → 16px
 * (animated by a 150ms transition). Both are driven by
 * `--entity-header-scroll-progress` (binary 0 | 1), set by the scroller in
 * HomeScene (see useHeaderScrollShrink). Below lg the padding snap is off.
 *
 * The avatar+text wrapper uses `items-start` when a subtitle is present so the
 * avatar tops with the title when the subtitle wraps to multiple lines (mobile
 * Account variant) — and `items-center` without one, so a lone title centers
 * against the taller avatar. The outer flex stays `items-center` so the
 * trailing actions cluster vertically centers against the avatar+text block.
 *
 * `sticky top-0` pins the header at the top of the page scroller while the body
 * scrolls beneath it; `bg-app` keeps it opaque so body content doesn't show
 * through, and `z-10` raises it (and its drop shadow) above the scrolling cards.
 *
 * `belowContent` (e.g. a tab bar) renders inside the header below the title
 * row, so it pins with the header and the drop shadow falls below it.
 */
const EntityPaneHeader = ({ title, subtitle, trailingContent, avatar, belowContent }: IEntityPaneHeaderProps) => {
  return (
    <div
      className={cx(
        'entity-header-scroll-shadow lg:entity-header-scroll-shrink bg-app sticky top-0 z-10 flex w-full flex-col px-4 pt-2 md:pt-8 lg:px-8',
        // belowContent (e.g. a tab bar) owns the header's bottom edge so its
        // underline sits flush against the scroll shadow — drop the gap.
        belowContent ? 'pb-0' : 'pb-2 md:pb-3'
      )}
    >
      <div className="flex w-full items-center justify-between">
        {/* items-start only when a subtitle can wrap — otherwise center the
            lone title against the (taller) avatar. */}
        <div className={cx('flex min-w-0 gap-3', subtitle ? 'items-start' : 'items-center')}>
          {avatar && <div className="flex shrink-0 items-start">{avatar}</div>}
          <div className="flex min-w-0 flex-col">
            <div className="text-primary md:text-display-sm truncate text-xl font-semibold">{title}</div>
            {subtitle && <div className="text-tertiary text-sm font-normal">{subtitle}</div>}
          </div>
        </div>
        {trailingContent && <div className="text-secondary flex shrink-0 text-sm">{trailingContent}</div>}
      </div>
      {belowContent && <div className="mt-5">{belowContent}</div>}
    </div>
  );
};

export default EntityPaneHeader;
